import { CommonModule } from '@angular/common';
import { Component, DestroyRef, ElementRef, inject, input, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed, takeUntilDestroyed as takeUntilDestroyedInterop } from '@angular/core/rxjs-interop';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ErrorMessageComponent, FormFieldComponent, PhoneNumberFieldComponent } from '@lx/quote-tool/lite/shared/ui/components';
import { AcquisitionDisclaimerComponent } from '@lx/quote-tool/shared/ui/components';
import { getCompany, getConfiguration } from '@lx/shared/tokens';
import { CompanyFormattedNamePipe } from '@lx/shared/ui/pipes';
import { CheckboxModule } from 'primeng/checkbox';
import { InputTextModule } from 'primeng/inputtext';
import { debounceTime, take } from 'rxjs/operators';
import { StudentDetails } from '@lx/quote-tool/shared/types';
import { OnlineQuoteToolStore } from '../../store';

@Component({
  standalone: true,
  selector: 'lx-enrolment-form',
  templateUrl: './enrolment-form.component.html',
  styleUrls: ['./enrolment-form.component.scss'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormFieldComponent,
    InputTextModule,
    CheckboxModule,
    ErrorMessageComponent,
    AcquisitionDisclaimerComponent,
    CompanyFormattedNamePipe,
    PhoneNumberFieldComponent,
  ],
})
export class EnrolmentFormComponent implements OnInit {
  @ViewChild('errorMessage') errorMessage: ElementRef;

  form = input.required<FormGroup>();
  submitted = input<boolean>(false);

  protected readonly company = getCompany();
  protected privacyPolicyLink: string | undefined;
  protected termsAndConditionsLink: string | undefined;

  private readonly store = inject(OnlineQuoteToolStore);
  private readonly destroyRef = inject(DestroyRef);
  private readonly configuration = getConfiguration();

  ngOnInit(): void {
    this.privacyPolicyLink = <string>this.configuration.QUOTE_TOOL_PRIVACY_POLICY_LINK;
    this.termsAndConditionsLink = <string>this.configuration.QUOTE_TOOL_TERMS_AND_CONDITIONS_LINK;
    this.setPhoneCodeBasedOnCountryOfResidence();
    this.patchInitialFormDataFromStore();
    this.subscribeToFormChanges();
  }

  private setPhoneCodeBasedOnCountryOfResidence() {
    this.store.phoneCode$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((phoneCode: string) => this.form().get('phoneNumber')?.setValue(phoneCode, { emitEvent: false }));
  }

  private subscribeToFormChanges(): void {
    /**
     * Subscribe to form value changes with debouncing to persist data without performance issues.
     * Debounce ensures we only persist after 500ms of inactivity (not on every keystroke).
     */
    this.form()
      .valueChanges.pipe(
        debounceTime(500), // Wait 500ms after last change before persisting
        takeUntilDestroyedInterop(this.destroyRef),
      )
      .subscribe((formValue) => {
        // Extract only user-editable student information fields
        const { firstName, lastName, email, phoneNumber, termsAndConditionsConsent, optOutMarketing } = formValue;

        // Update student details with form information
        this.store.updateStudentInformation({
          firstName,
          lastName,
          email,
          phoneNumber,
          termsAndConditionsConsent,
          consentMarketing: !optOutMarketing,
        });
      });
  }

  private patchInitialFormDataFromStore(): void {
    this.store.studentDetails$
      .pipe(take(1), takeUntilDestroyedInterop(this.destroyRef))
      .subscribe((studentDetails) => this.patchStudentDetails(studentDetails));
  }

  private patchStudentDetails(studentDetails: StudentDetails | undefined): void {
    if (!studentDetails) {
      return;
    }
    const { consentMarketing, ...otherDetails } = studentDetails;

    if (consentMarketing !== undefined) {
      this.patchFormControl('optOutMarketing', !consentMarketing);
    }

    Object.keys(otherDetails).forEach((key) => {
      const value = otherDetails[key as keyof typeof otherDetails];
      if (value !== undefined && value !== null) {
        this.patchFormControl(key, value);
      }
    });
  }

  /**
   * Helper method to patch a form control with a value without triggering the form valueChanges subscription.
   * @param controlName The form control name to patch
   * @param value The value to set
   */
  private patchFormControl(controlName: string, value: unknown): void {
    const control = this.form().get(controlName);
    if (control) {
      control.setValue(value, { emitEvent: false });
      control.markAsTouched();
      control.updateValueAndValidity({ emitEvent: false });
    }
  }
}
